<template>
  <div class="syscfg">
    <div class="syscfg__intro">
      <i class="bi bi-info-circle"></i>
      <div v-if="intro">{{ intro }}</div>
      <div v-else>
        Toàn bộ cấu hình hệ thống (MongoDB collection <code>system_config</code>), trừ các mức xu tác giả đã có ở tab
        <b>Xu &amp; phúc lợi tác giả</b> và <b>BXH tác giả</b>. Cấu hình mới thêm ở backend chưa được phân nhóm sẽ tự hiện ở nhóm <b>Khác</b>.
      </div>
    </div>

    <div v-if="loading" class="syscfg__loading"><span class="spinner-border text-primary"></span></div>

    <template v-else>
      <section v-for="g in groupsView" :key="g.title" class="syscfg__section"
               :class="{'is-unused-group': g.allUnused}">
        <h3 class="syscfg__title">{{ g.title }}<span v-if="g.allUnused" class="syscfg__tag ms-2">chưa dùng</span></h3>
        <p v-if="g.note" class="syscfg__note">{{ g.note }}</p>
        <div class="syscfg__grid">
          <component :is="f.type === 'list' ? 'div' : 'label'" v-for="f in g.fields" :key="f.id" class="syscfg__field"
                 :class="{'is-dirty': isDirty(f.id), 'is-unused': f.unused, 'syscfg__field--wide': f.type === 'list'}">
            <span class="syscfg__label">
              {{ f.label }}
              <span v-if="f.unused" class="syscfg__tag">chưa dùng</span>
              <span v-if="f.missing" class="syscfg__tag" title="API hiện tại chưa có cấu hình này – cần deploy backend mới thì mới lưu được">backend chưa hỗ trợ</span>
            </span>
            <!-- Danh sách theo từng top: Top 1, Top 2… thêm / bớt được -->
            <div v-if="f.type === 'list'" class="syscfg__tops">
              <div v-for="(val, i) in form[f.id]" :key="i" class="syscfg__top">
                <span class="syscfg__top-rank" :class="'is-top' + (i + 1)">{{ f.itemLabel || 'Top' }} {{ i + 1 }}</span>
                <NumberInput :model-value="val" placeholder="0" @update:model-value="setListItem(f.id, i, $event)"/>
                <span v-if="f.unit" class="syscfg__unit">{{ f.unit }}</span>
                <button type="button" class="syscfg__top-del" title="Xoá top này" @click="removeListItem(f.id, i)">
                  <i class="bi bi-x-lg"></i>
                </button>
              </div>
              <div class="syscfg__top-foot">
                <button type="button" class="syscfg__top-add" :disabled="f.maxItems && (form[f.id] || []).length >= f.maxItems"
                        @click="addListItem(f.id)">
                  <i class="bi bi-plus-lg"></i> Thêm top {{ (form[f.id] || []).length + 1 }}
                  <small v-if="f.maxItems">(tối đa {{ f.maxItems }})</small>
                </button>
                <!-- Tạm tính tổng phải trả nếu đủ người nhận ở mọi top -->
                <div v-if="(form[f.id] || []).length" class="syscfg__top-total">
                  Tạm tính tổng: <b>{{ $numberFormat(listTotal(f.id)) }}</b> {{ f.unit }}
                  <small>/ {{ (form[f.id] || []).length }} top</small>
                </div>
              </div>
              <div v-if="!(form[f.id] || []).length" class="syscfg__top-empty">Chưa có top nào – sẽ không trao giải</div>
            </div>
            <div v-else class="syscfg__input">
              <input v-if="f.type === 'str'" type="text" v-model.trim="form[f.id]" :placeholder="f.placeholder || ''">
              <NumberInput v-else v-model="form[f.id]" :max="f.max" :placeholder="f.placeholder || ''"/>
              <span v-if="f.unit" class="syscfg__unit">{{ f.unit }}</span>
            </div>
            <small class="syscfg__hint"><code>{{ f.id }}</code><template v-if="f.hint"> · {{ f.hint }}</template></small>
          </component>
        </div>
      </section>

      <div class="syscfg__bar">
        <span v-if="dirtyIds.length" class="syscfg__changes">Có {{ dirtyIds.length }} thay đổi chưa lưu</span>
        <span v-else class="syscfg__changes text-muted">Không có thay đổi</span>
        <button class="btn btn-light" :disabled="!dirtyIds.length || saving" @click="reset()">Hoàn tác</button>
        <button class="btn btn-primary" :disabled="!dirtyIds.length || saving" @click="save()">
          <span v-if="saving" class="spinner-border spinner-border-sm me-1"></span>
          Lưu thay đổi
        </button>
      </div>
    </template>
  </div>
</template>

<script>
import requestUtil from "@/utils/request-util";
import NumberInput from "@/components/commons/NumberInput.vue";
import {RANKING_IDS} from "@/components/settings/rankingConfig";

// Các id đã quản lý ở tab "Xu & phúc lợi tác giả" – không lặp lại ở đây
const WELFARE_IDS = [
  'AUTHOR_PRICE_VIEW_RANK_1', 'AUTHOR_PRICE_VIEW_RANK_2', 'AUTHOR_PRICE_VIEW_RANK_3', 'AUTHOR_PRICE_VIEW_RANK_4',
  'AUTHOR_PRICE_VIEW_EXCLUSIVE', 'AUTHOR_PRICE_VIEW_APP',
  'COMPOSER_1K_VIEW', 'COMPOSER_5K_VIEW', 'COMPOSER_10K_VIEW', 'COMPOSER_100K_VIEW',
  'TRANSLATE_1K_VIEW', 'TRANSLATE_5K_VIEW', 'TRANSLATE_10K_VIEW', 'TRANSLATE_100K_VIEW',
  'FEE_ORDER_PERCENT', 'FEE_DONATE_PERCENT',
];

// type: num (value) | str (valueStr) | list (values – danh sách số)
const GROUPS = [
  {
    title: 'Lượt đọc', fields: [
      {id: 'VIEW_RATE', label: 'Tỉ lệ lượt đọc được tính', unit: '%', max: 100,
        hint: 'Áp dụng cho truyện ≥ 2.000 lượt đọc. Cache 5 phút'},
      {id: 'VIEW', label: 'Điểm thưởng người đọc mỗi lượt đọc', unit: 'điểm', unused: true},
    ],
  },
  {
    title: 'Người dùng', fields: [
      {id: 'REFERRAL', label: 'Thưởng giới thiệu bạn bè', unit: 'xu'},
      {id: 'ROLL_CALL_FROM', label: 'Điểm danh – tối thiểu', unit: 'điểm'},
      {id: 'ROLL_CALL_TO', label: 'Điểm danh – tối đa', unit: 'điểm'},
      {id: 'REGISTER', label: 'Thưởng đăng ký tài khoản', unit: 'xu', unused: true},
      {id: 'MIN_WITHDRAW', label: 'Số xu rút tối thiểu', unit: 'xu', placeholder: '200.000',
        hint: 'Áp dụng cho mọi yêu cầu rút (web & app). Mặc định 200.000'},
    ],
  },
  {
    title: 'Đề cử', fields: [
      {id: 'STORY_NOMINATION_COUNT_FOR_ACCOUNT_FREE', label: 'Số đề cử – tài khoản thường', unit: 'lượt'},
      {id: 'STORY_NOMINATION_COUNT_FOR_ACCOUNT_PREMIUM', label: 'Số đề cử – tài khoản Siêu Mọt', unit: 'lượt'},
    ],
  },
  {
    title: 'Thưởng truyện top lượt đọc tuần',
    note: 'Hiện không trả xu: job storyChartWeek đã tắt phần xử lý.',
    fields: [
      {id: 'AWARD_TOP_1_VIEW_WEEK_VALUE', label: 'Top 1 lượt đọc tuần', unit: 'xu', unused: true},
      {id: 'AWARD_TOP_2_VIEW_WEEK_VALUE', label: 'Top 2 lượt đọc tuần', unit: 'xu', unused: true},
      {id: 'AWARD_TOP_3_VIEW_WEEK_VALUE', label: 'Top 3 lượt đọc tuần', unit: 'xu', unused: true},
    ],
  },
  {
    title: 'Giới thiệu tác giả (affiliate)', fields: [
      {id: 'AWARD_AFFILIATE_AUTHOR_10K', label: 'Đạt 10K lượt đọc', unit: 'xu', unused: true},
      {id: 'AWARD_AFFILIATE_AUTHOR_50K', label: 'Đạt 50K lượt đọc', unit: 'xu', unused: true},
      {id: 'AWARD_AFFILIATE_AUTHOR_100K', label: 'Đạt 100K lượt đọc', unit: 'xu', unused: true},
      {id: 'AWARD_AFFILIATE_AUTHOR_200K', label: 'Đạt 200K lượt đọc', unit: 'xu', unused: true},
      {id: 'AWARD_AFFILIATE_AUTHOR_500K', label: 'Đạt 500K lượt đọc', unit: 'xu', unused: true},
    ],
  },
  {
    title: 'Gói Siêu Mọt', fields: [
      {id: 'FEE_PREMIUM_ACCOUNT_WEEK', label: 'Giá gói 1 tuần', unit: 'xu', unused: true},
      {id: 'FEE_PREMIUM_ACCOUNT_1_MONTH', label: 'Giá gói 1 tháng', unit: 'xu', unused: true},
      {id: 'FEE_PREMIUM_ACCOUNT_3_MONTH', label: 'Giá gói 3 tháng', unit: 'xu', unused: true},
    ],
  },
  {
    title: 'Quảng cáo trên app', note: 'Khoảng cách giữa 2 lần hiển thị (để trống = mặc định của backend).', fields: [
      {id: 'ADMOB_BANNER_RATE', label: 'Banner', unit: 'giây', hint: 'Mặc định 20'},
      {id: 'ADMOB_INTERLEAVED_RATE', label: 'Xen kẽ', unit: 'giây', hint: 'Mặc định 180'},
      {id: 'ADMOB_INTERSTITIAL_RATE', label: 'Toàn màn hình', unit: 'giây', hint: 'Mặc định 180'},
      {id: 'ADMOB_REWARDED_RATE', label: 'Quảng cáo thưởng', unit: 'giây', hint: 'Mặc định 180'},
      {id: 'ADMOB_ADVANCED_RATE', label: 'Nâng cao (native)', unit: 'giây', hint: 'Mặc định 180'},
      {id: 'ADMOB_OPEN_APP_RATE', label: 'Mở app', unit: 'giây', hint: 'Mặc định 180'},
      {id: 'AFFILIATE_BANNER_PERIOD', label: 'Affiliate banner', unit: 'giây', unused: true},
      {id: 'AFFILIATE_PRODUCT_PERIOD', label: 'Affiliate sản phẩm', unit: 'giây', unused: true},
    ],
  },
  {
    title: 'Phiên bản app', note: 'App thấp hơn phiên bản tối thiểu sẽ bị yêu cầu cập nhật.', fields: [
      {id: 'APP_IOS_REQUIRED_VERSION', label: 'iOS – phiên bản tối thiểu', type: 'str', placeholder: '2.0.1'},
      {id: 'APP_ANDROID_REQUIRED_VERSION', label: 'Android – phiên bản tối thiểu', type: 'str', placeholder: '2.0.1'},
    ],
  },
  {
    title: 'Cấu hình cũ', note: 'Không còn dùng để trả xu (đã thay bằng xu theo hạng ở tab Xu & phúc lợi tác giả).', fields: [
      {id: 'AUTHOR_COMPOSED', label: 'Xu mỗi lượt đọc – truyện sáng tác', unit: 'xu', unused: true},
      {id: 'AUTHOR_TRANSLATE', label: 'Xu mỗi lượt đọc – truyện dịch', unit: 'xu', unused: true},
      {id: 'AWARD_AUTHOR_TOP_MONTH_EXCELLENT_1', label: 'Top 1 tác giả xuất sắc (cũ)', unit: 'xu', unused: true},
      {id: 'AWARD_AUTHOR_TOP_MONTH_EXCELLENT_2', label: 'Top 2 tác giả xuất sắc (cũ)', unit: 'xu', unused: true},
      {id: 'AWARD_AUTHOR_TOP_MONTH_EXCELLENT_3', label: 'Top 3 tác giả xuất sắc (cũ)', unit: 'xu', unused: true},
      {id: 'AWARD_AUTHOR_TOP_MONTH_FAVORITE_1', label: 'Top 1 tác giả yêu thích (cũ)', unit: 'xu', unused: true},
      {id: 'AWARD_AUTHOR_TOP_MONTH_HARD_WORKING_1', label: 'Top 1 tác giả chăm chỉ (cũ)', unit: 'xu', unused: true},
    ],
  },
];

export default {
  name: 'SystemConfigSettings',
  components: {NumberInput},
  props: {
    /** Truyền nhóm riêng (VD: tab BXH tác giả). Bỏ trống = toàn bộ cấu hình hệ thống */
    groups: {type: Array, default: null},
    intro: {type: String, default: null},
  },
  data() {
    return {
      loading: true,
      saving: false,
      groupsView: [],
      fieldsById: {},
      raw: {},
      original: {},
      form: {},
    };
  },
  computed: {
    dirtyIds() {
      return Object.keys(this.fieldsById).filter((id) => this.isDirty(id));
    },
  },
  mounted() {
    this.load();
  },
  methods: {
    toForm(f, c) {
      if (f.type === 'list') return c && Array.isArray(c.values) ? c.values.slice() : [];
      if (!c) return f.type === 'num' || !f.type ? null : '';
      if (f.type === 'str') return c.valueStr ?? '';
      return c.value ?? null;
    },
    norm(f, v) {
      if (f.type === 'str') return (v ?? '').trim();
      if (f.type === 'list') return (Array.isArray(v) ? v : []).map((x) => String(x ?? '').trim()).join(',');
      return v === '' || v === null || v === undefined ? null : Number(v);
    },
    isDirty(id) {
      const f = this.fieldsById[id];
      return this.norm(f, this.form[id]) !== this.norm(f, this.original[id]);
    },
    async load() {
      this.loading = true;
      try {
        const [idsRes, cfgRes] = await Promise.all([
          requestUtil.get('/admin/configSystemIds'),
          requestUtil.get('/admin/configSystem'),
        ]);
        const byId = {};
        (cfgRes.data || []).forEach((c) => (byId[c.id] = c));
        this.raw = byId;

        // id backend có nhưng chưa phân nhóm → nhóm "Khác"
        const source = this.groups || GROUPS;
        const known = new Set([...WELFARE_IDS, ...RANKING_IDS, ...GROUPS.flatMap((g) => g.fields.map((f) => f.id))]);
        const others = this.groups ? [] : (idsRes.data || [])
          .filter((x) => !known.has(x.key))
          .map((x) => ({id: x.key, label: x.description || x.key}));
        const backendIds = new Set((idsRes.data || []).map((x) => x.key));

        const groups = source
          // vẫn hiện ô backend chưa có (chưa deploy) để nhập trước, đánh dấu cho admin biết
          .map((g) => ({...g, fields: g.fields.map((f) => ({...f, missing: backendIds.size > 0 && !backendIds.has(f.id)}))}))
          .filter((g) => g.fields.length)
          .map((g) => ({...g, allUnused: g.fields.every((f) => f.unused)}))
          // nhóm toàn cấu hình chưa dùng xuống cuối
          .sort((a, b) => Number(a.allUnused) - Number(b.allUnused));
        if (others.length) groups.push({title: 'Khác', fields: others});

        const fieldsById = {};
        const values = {};
        groups.forEach((g) => g.fields.forEach((f) => {
          fieldsById[f.id] = f;
          values[f.id] = this.toForm(f, byId[f.id]);
        }));
        this.groupsView = groups;
        this.fieldsById = fieldsById;
        this.original = this.clone(values);
        this.form = this.clone(values);
      } catch (e) {
        this.$notify({title: 'Không tải được cấu hình', text: e?.message || '', type: 'warn'});
      } finally {
        this.loading = false;
      }
    },
    clone(obj) {
      return JSON.parse(JSON.stringify(obj));
    },
    setListItem(id, i, value) {
      const list = (this.form[id] || []).slice();
      list[i] = value === null || value === '' ? '' : Number(value);
      this.form[id] = list;
    },
    listTotal(id) {
      return (this.form[id] || []).reduce((sum, v) => sum + (Number(v) || 0), 0);
    },
    addListItem(id) {
      const list = (this.form[id] || []).slice();
      list.push(list.length ? list[list.length - 1] : 0);
      this.form[id] = list;
    },
    removeListItem(id, i) {
      const list = (this.form[id] || []).slice();
      list.splice(i, 1);
      this.form[id] = list;
    },
    reset() {
      this.form = this.clone(this.original);
    },
    validate(id) {
      const f = this.fieldsById[id];
      const v = this.form[id];
      if (f.type === 'str') return true;
      if (f.type === 'list') {
        return (Array.isArray(v) ? v : []).every((x) => /^\d+$/.test(String(x ?? '').trim()));
      }
      if (v === null || v === '') return true;
      if (!Number.isInteger(v) || v < (f.min ?? 0)) return false;
      return f.max === undefined || v <= f.max;
    },
    async save() {
      const invalid = this.dirtyIds.filter((id) => !this.validate(id));
      if (invalid.length) {
        this.$notify({title: 'Giá trị không hợp lệ', text: invalid.join(', '), type: 'warn'});
        return;
      }
      if (!window.confirm(`Lưu ${this.dirtyIds.length} thay đổi cấu hình hệ thống?`)) return;
      this.saving = true;
      let ok = 0;
      for (const id of this.dirtyIds) {
        const f = this.fieldsById[id];
        const old = this.raw[id] || {};
        const body = {id, value: old.value ?? null, valueStr: old.valueStr ?? null, values: old.values ?? null};
        const v = this.form[id];
        if (f.type === 'str') body.valueStr = (v || '').trim() || null;
        else if (f.type === 'list') body.values = (Array.isArray(v) ? v : []).map(Number);
        else body.value = v === '' || v === null ? null : Number(v);
        try {
          const res = await requestUtil.post('/admin/configMoney/_single', body);
          this.raw[id] = res?.data || {...old, ...body};
          this.original[id] = Array.isArray(v) ? v.slice() : v;
          ok++;
        } catch (e) {
          this.$notify({title: `Lưu ${id} thất bại`, text: e?.message || '', type: 'warn'});
        }
      }
      this.saving = false;
      if (ok) this.$notify({title: `Đã lưu ${ok} thay đổi`, type: 'success'});
    },
  },
};
</script>

<style scoped>
.syscfg { display: flex; flex-direction: column; gap: 20px; }
.syscfg__intro {
  display: flex; gap: 10px; padding: 12px 16px; border-radius: 10px;
  background: #e9f3ff; color: #1b5fae; font-size: 13px; line-height: 1.6;
}
.syscfg__intro i { font-size: 16px; margin-top: 1px; }
.syscfg__loading { padding: 40px; text-align: center; }
.syscfg__section { padding: 18px 20px; border: 1px solid #eff2f5; border-radius: 12px; background: #fff; }
.syscfg__title { margin: 0 0 6px; font-size: 15px; font-weight: 700; color: #181c32; }
.syscfg__note { margin: 0 0 12px; font-size: 13px; color: #7e8299; }
.syscfg__grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 12px; margin-top: 12px; }
.syscfg__field {
  display: flex; flex-direction: column; gap: 8px; padding: 12px 14px; margin: 0;
  border: 1px solid #eff2f5; border-radius: 10px; background: #fafbfc;
  transition: border-color .15s ease, background-color .15s ease;
}
.syscfg__field.is-unused { opacity: .7; }
/* cả nhóm chưa dùng: xám hẳn */
.syscfg__section.is-unused-group {
  background: #f7f7f9;
  border-style: dashed;
  filter: grayscale(1);
  opacity: .65;
}
.syscfg__section.is-unused-group .syscfg__field { background: #f1f1f4; opacity: 1; }
.syscfg__section.is-unused-group input { background: #f7f7f9; color: #7e8299; }
.syscfg__label { display: flex; align-items: center; gap: 6px; flex-wrap: wrap; font-size: 13px; font-weight: 600; color: #3f4254; }
.syscfg__tag {
  padding: 1px 6px; border-radius: 6px; background: #f1f1f4; color: #99a1b7;
  font-size: 11px; font-weight: 600;
}
.syscfg__input { display: flex; align-items: center; gap: 8px; }
.syscfg input {
  width: 100%; min-width: 80px; height: 38px; padding: 0 10px;
  border: 1px solid #e4e6ef; border-radius: 8px; background: #fff;
  font-size: 14px; font-weight: 600; color: #181c32; outline: none;
}
.syscfg input:focus { border-color: #1b84ff; }
.syscfg__unit { flex: 0 0 auto; font-size: 12px; color: #7e8299; white-space: nowrap; }
.syscfg__hint { font-size: 11px; color: #a1a5b7; word-break: break-all; }
.syscfg__hint code { font-size: 11px; color: #a1a5b7; background: none; padding: 0; }
.is-dirty { border-color: #e9a800 !important; background: #fff9e6 !important; opacity: 1 !important; }
.syscfg__bar {
  position: sticky; bottom: 0; z-index: 2;
  display: flex; align-items: center; justify-content: flex-end; gap: 10px;
  padding: 12px 16px; border-radius: 12px; background: #fff;
  box-shadow: 0 -4px 16px rgba(24, 28, 50, .08);
}
.syscfg__changes { margin-right: auto; font-size: 13px; font-weight: 600; color: #b07d00; }
@media (max-width: 1199.98px) { .syscfg__grid { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
@media (max-width: 575.98px) { .syscfg__grid { grid-template-columns: 1fr; } }

/* Danh sách giải theo top */
.syscfg__field--wide { grid-column: 1 / -1; }
/* mỗi top một dòng, xếp dọc */
.syscfg__tops { display: flex; flex-direction: column; gap: 8px; max-width: 520px; }
.syscfg__top { display: flex; align-items: center; gap: 8px; }
.syscfg__top input { flex: 1 1 auto; }
.syscfg__top-rank {
  flex: 0 0 64px; height: 38px; display: inline-flex; align-items: center; justify-content: center;
  border-radius: 8px; background: #f1f1f4; color: #5e6278; font-size: 13px; font-weight: 700;
}
.syscfg__top-rank.is-top1 { background: #fff4d6; color: #b07d00; }
.syscfg__top-rank.is-top2 { background: #eef0f4; color: #5e6278; }
.syscfg__top-rank.is-top3 { background: #fdeee3; color: #b8541b; }
.syscfg__top-del {
  flex: 0 0 34px; height: 34px; border: 0; border-radius: 8px; background: transparent; color: #a1a5b7;
}
.syscfg__top-del:hover { background: #fff0f3; color: #f8285a; }
.syscfg__top-add {
  align-self: flex-start;
  padding: 0 16px;
  display: inline-flex; align-items: center; justify-content: center; gap: 6px; height: 38px;
  border: 1px dashed #b5d5ff; border-radius: 8px; background: #f5f9ff; color: #1b84ff; font-size: 13px; font-weight: 600;
}
.syscfg__top-add small { color: #7e8299; font-weight: 400; }
.syscfg__top-add:disabled { opacity: .5; cursor: not-allowed; }
.syscfg__top-foot { display: flex; flex-wrap: wrap; align-items: center; justify-content: space-between; gap: 10px; }
.syscfg__top-total { font-size: 13px; color: #4b5675; }
.syscfg__top-total b { font-size: 15px; color: #1b84ff; }
.syscfg__top-total small { color: #a1a5b7; }
.syscfg__top-empty { grid-column: 1 / -1; font-size: 12px; color: #f8285a; }
</style>
